import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { Bug, Loader2 } from 'lucide-react'
import { formatNumber } from '../utils'
import type {AiDebugSourceResponse} from '@/lib/api/features/ai';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {

  getDebugDocument,
  getDebugWorkItem
} from '@/lib/api/features/ai'

interface Props {
  workspaceSlug: string
}

type DebugType = 'workItem' | 'document'

/**
 * Card 4 — Composed-embedding inspection.
 *
 * Paste-ID lookup only (no autocomplete in v1 — the plan deferred RxDB-based
 * title search to v1.1 to avoid coupling the debug surface to the semantic
 * search index, which may be disabled in the exact context where an admin
 * needs the debug view). The endpoint re-runs the builder live, so the
 * `fullText` returned is exactly what the LLM would see on the next pass —
 * useful for verifying that a cascade trigger fired without scraping pgvector.
 */
export function EmbeddingDebugCard({ workspaceSlug }: Props) {
  const { t } = useTranslation('settings')
  const [type, setType] = useState<DebugType>('workItem')
  const [id, setId] = useState('')

  const mutation = useMutation({
    mutationFn: async (input: {
      type: DebugType
      id: string
    }): Promise<AiDebugSourceResponse> => {
      return input.type === 'workItem'
        ? getDebugWorkItem(workspaceSlug, input.id)
        : getDebugDocument(workspaceSlug, input.id)
    },
  })

  const handleLookup = (event: React.FormEvent) => {
    event.preventDefault()
    const trimmed = id.trim()
    if (!trimmed) return
    mutation.mutate({ type, id: trimmed })
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Bug className="size-4 text-muted-foreground" />
          <CardTitle>{t('aiSettings.debug.title')}</CardTitle>
        </div>
        <CardDescription>{t('aiSettings.debug.description')}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <form onSubmit={handleLookup} className="grid gap-3 sm:grid-cols-[160px_1fr_auto] sm:items-end">
          <div className="space-y-2">
            <Label htmlFor="debug-source-type">
              {t('aiSettings.debug.typeLabel')}
            </Label>
            <Select value={type} onValueChange={(v: DebugType) => setType(v)}>
              <SelectTrigger id="debug-source-type">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="workItem">
                  {t('aiSettings.debug.typeWorkItem')}
                </SelectItem>
                <SelectItem value="document">
                  {t('aiSettings.debug.typeDocument')}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label htmlFor="debug-source-id">
              {t('aiSettings.debug.idLabel')}
            </Label>
            <Input
              id="debug-source-id"
              placeholder={t('aiSettings.debug.idPlaceholder')}
              value={id}
              onChange={(e) => setId(e.target.value)}
            />
          </div>
          <Button
            type="submit"
            disabled={mutation.isPending || !id.trim()}
          >
            {mutation.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : null}
            {t('aiSettings.debug.lookup')}
          </Button>
        </form>

        {mutation.isError ? (
          <p className="text-sm text-destructive">
            {(mutation.error as any)?.response?.data?.message ??
              t('aiSettings.debug.errorFallback')}
          </p>
        ) : null}

        {mutation.data ? <DebugResult data={mutation.data} /> : null}
      </CardContent>
    </Card>
  )
}

function DebugResult({ data }: { data: AiDebugSourceResponse }) {
  const { t } = useTranslation('settings')
  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_280px]">
      <div className="rounded-md border bg-muted/40 p-3">
        <pre className="max-h-96 overflow-auto whitespace-pre-wrap break-words font-mono text-xs leading-relaxed">
          {data.fullText}
        </pre>
      </div>
      <dl className="space-y-3 rounded-md border p-3 text-sm">
        <DebugStat
          label={t('aiSettings.debug.statName')}
          value={data.name ?? '—'}
        />
        {data.identifier ? (
          <DebugStat
            label={t('aiSettings.debug.statIdentifier')}
            value={data.identifier}
          />
        ) : null}
        <DebugStat
          label={t('aiSettings.debug.statChunkCount')}
          value={formatNumber(data.chunkCount)}
        />
        <DebugStat
          label={t('aiSettings.debug.statCharCount')}
          value={formatNumber(data.charCount)}
        />
        <DebugStat
          label={t('aiSettings.debug.statEstimatedTokens')}
          value={`~${formatNumber(data.estimatedTokens)}`}
        />
        <DebugStat
          label={t('aiSettings.debug.statContentHash')}
          value={data.contentHash.slice(0, 12) + '…'}
          title={data.contentHash}
        />
      </dl>
    </div>
  )
}

function DebugStat({
  label,
  value,
  title,
}: {
  label: string
  value: string
  title?: string
}) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className="mt-0.5 font-mono text-xs break-all"
        title={title}
      >
        {value}
      </dd>
    </div>
  )
}
